import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Home, Loader2 } from "lucide-react";
import { updateCustomers } from "@/services/auth";
import { getT } from "@/i18n/translations";


export default function SloganPage() {
  const t = getT();
  const navigate = useNavigate();
  const MAX_LENGTH = 100;
  const token = useSelector((state) => state.auth.token);
  const { data: customer } = useSelector((state) => state.customer);
  const customerCode = localStorage.getItem("customercode");

  const [slogan, setSlogan] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!slogan.trim()) { setError(t.slogan_errEmpty); return; }
    setError("");
    setLoading(true);
    try {
      const res = await updateCustomers(token, customerCode, { slogan: slogan.trim() });
      if (res) setShowPopup(true);
      else setError(t.slogan_errFailed);
    } catch {
      setError(t.slogan_errFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
   className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
<div className="w-full max-w-md min-h-screen flex flex-col">

        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 text-[#1a2e1a]" />
          </button>
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.slogan_pageTitle}</p>
          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <Home className="w-5 h-5 text-[#1a2e1a]" />
          </button>
        </div>

   <div className="px-4 pb-8 space-y-3 flex-1">

          {customer?.customer?.slogan ? (
            /* ── Already submitted ── */
            <div
              className="rounded-3xl p-6 relative overflow-hidden"
              style={{ background: 'linear-gradient(160deg, #2d5a2d 0%, #1a3a1a 60%, #3a6b1a 100%)' }}
            >
              <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white/5" />
              <div className="absolute -bottom-10 -left-6 w-28 h-28 rounded-full bg-white/5" />
              <div className="relative z-10 text-center">
                <div className="w-16 h-16 rounded-full bg-[#a8d08a]/20 border-2 border-[#a8d08a]/40 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🏆</span>
                </div>
                <h2 className="text-[22px] font-bold text-white mb-3">{t.slogan_alreadyTitle}</h2>
                <p className="text-[13px] text-white/70 leading-relaxed mb-5">{t.slogan_alreadyMsg}</p>
                <button
                  onClick={() => navigate("/home")}
                  className="rounded-2xl py-3 px-4 text-[13px] font-semibold text-[#1a2e00]"
                  style={{ backgroundColor: "#c8d400" }}
                >
                  {t.slogan_goHome}
                </button>
              </div>
            </div>

          ) : (
            /* ── Submit slogan ── */
            <>
              {/* Green header card */}
              <div
                className="rounded-3xl p-5 relative overflow-hidden"
                style={{ background: 'linear-gradient(160deg, #2d5a2d 0%, #1a3a1a 60%, #3a6b1a 100%)' }}
              >
                <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white/5" />
                <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-white/5" />
                <div className="relative z-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#a8d08a]/20 border-2 border-[#a8d08a]/40 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">✏️</span>
                  </div>
                  <p className="text-[11px] font-semibold tracking-widest text-[#a8d08a] uppercase mb-1">
                    {t.slogan_contestLabel}
                  </p>
                  <h2 className="text-[20px] font-bold text-white mb-2">{t.slogan_enterTitle}</h2>
                  <p className="text-[12px] text-white/60 leading-relaxed">{t.slogan_enterSubtitle}</p>
                </div>
              </div>

              {/* Input card */}
              <div className="bg-white rounded-2xl px-5 py-5 shadow-sm">
                <label className="text-[12px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-2 block">
                  {t.slogan_inputLabel}
                </label>
                <textarea
                  rows={4}
                  value={slogan}
                  maxLength={MAX_LENGTH}
                  onChange={(e) => { setSlogan(e.target.value); setError(""); }}
                  placeholder={t.slogan_placeholder}
                  className="w-full border-[1.5px] border-[#e0e8e0] rounded-xl px-4 py-3 text-sm text-[#1a2e1a] resize-none outline-none focus:border-[#3a6b3a] transition-colors placeholder:text-[#b0c4b0]"
                />
                <div className="flex justify-between items-center mt-2">
                  {error ? (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <span>⚠</span> {error}
                    </p>
                  ) : <div />}
                  <div
                    className={`text-[11px] font-medium px-2 py-1 rounded-full ${slogan.length > 90
                        ? "bg-red-100 text-red-600"
                        : slogan.length > 70
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                  >
                    {slogan.length}/{MAX_LENGTH}
                  </div>
                </div>
              </div>

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={loading || !slogan.trim()}
                className={`w-full py-4 rounded-2xl text-[15px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${loading || !slogan.trim() ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{ backgroundColor: '#2d5a2d' }}
              >
                {loading ? (
                  <><Loader2 className="animate-spin h-4 w-4" /> {t.slogan_submitting}</>
                ) : (
                  t.slogan_submit
                )}
              </button>
            </>
          )}

        

        </div>
          <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>

      {/* ── Thank You Popup ── */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-7 w-full max-w-sm text-center mx-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(168,208,138,0.2)', border: '2px solid rgba(168,208,138,0.4)' }}
            >
              <span className="text-3xl">🎉</span>
            </div>
            <p className="text-[11px] font-semibold tracking-widest text-[#4a7c59] uppercase mb-2">
              {t.slogan_popupLabel}
            </p>
            <h2 className="text-[20px] font-bold text-[#1a2e1a] mb-2">{t.slogan_popupTitle}</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">{t.slogan_popupMsg}</p>
            <button
              onClick={() => { setShowPopup(false); navigate("/"); }}
              className="w-full text-white py-3.5 rounded-xl text-[14px] font-semibold active:scale-[0.98] transition-transform"
              style={{ backgroundColor: '#2d5a2d' }}
            >
              {t.slogan_goHome}
            </button>
          </div>
        </div>
      )}
       
    </div>
  );
}